import { type ReactNode, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Menu, X, Loader2 } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/Layout";
import {
  useAutoTranslate,
  collectBlockStrings,
  applyBlockStrings,
} from "@/components/AutoTranslate";

// Real documentation shell: a persistent sidebar of every doc page + a
// content area with a structured block renderer (including code). Responsive:
// the sidebar collapses into a toggle on mobile.

export type DocBlock =
  | { type: "p"; text: ReactNode }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: ReactNode[] }
  | { type: "ol"; items: ReactNode[] }
  | { type: "code"; lang?: string; code: string }
  | { type: "callout"; title?: string; text: ReactNode }
  | { type: "table"; head: string[]; rows: ReactNode[][] };

export type DocMeta = { slug: string; title: string; description: string };

// The single source of truth for the docs navigation + prev/next order.
export const DOC_NAV: { group: string; items: DocMeta[] }[] = [
  {
    group: "Getting started",
    items: [
      { slug: "", title: "Overview", description: "What Reality Twin is and how the pieces fit." },
      {
        slug: "quickstart",
        title: "Quickstart",
        description: "Create your first AI Twin in about 60 seconds.",
      },
      {
        slug: "knowledge-base",
        title: "Knowledge base",
        description: "Crawl your site, upload docs, and how grounding works.",
      },
      {
        slug: "twin-personality",
        title: "Twin personality",
        description: "Tune voice, behaviour and how the twin qualifies and hands off.",
      },
    ],
  },
  {
    group: "Deploy & integrate",
    items: [
      {
        slug: "embed-widget",
        title: "Embed widget",
        description: "Add the chat widget to any website with one script tag.",
      },
      {
        slug: "leads-and-crm",
        title: "Leads & CRM webhooks",
        description: "Capture leads and stream them to your CRM, HMAC-signed.",
      },
      {
        slug: "mcp-server",
        title: "MCP server",
        description: "Connect ChatGPT, Claude and agents to the network.",
      },
    ],
  },
  {
    group: "Platform",
    items: [
      {
        slug: "exchange",
        title: "Business Exchange",
        description: "Listings, nightly matching and the marketplace.",
      },
      {
        slug: "plans-and-usage",
        title: "Plans & usage",
        description: "The shared conversation pool, limits and multi-twin.",
      },
      {
        slug: "security",
        title: "Security & data",
        description: "Hosting, isolation, GDPR and the DPA.",
      },
    ],
  },
];

const FLAT: DocMeta[] = DOC_NAV.flatMap((g) => g.items);

function docPath(slug: string) {
  return slug ? `/docs/${slug}` : "/docs";
}

function Blocks({ blocks }: { blocks: DocBlock[] }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.type) {
          case "p":
            return (
              <p key={i} className="mt-4 text-[15.5px] leading-[1.75] text-muted-foreground">
                {b.text}
              </p>
            );
          case "h2":
            return (
              <h2
                key={i}
                className="mt-12 font-serif text-[26px] sm:text-[30px] leading-[1.1] tracking-[-0.02em]"
              >
                {b.text}
              </h2>
            );
          case "h3":
            return (
              <h3 key={i} className="mt-8 font-medium text-[18px]">
                {b.text}
              </h3>
            );
          case "ul":
            return (
              <ul
                key={i}
                className="mt-4 space-y-2 text-[15px] text-muted-foreground list-disc pl-5"
              >
                {b.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol
                key={i}
                className="mt-4 space-y-2.5 text-[15px] text-muted-foreground list-decimal pl-5"
              >
                {b.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ol>
            );
          case "code":
            return (
              <pre
                key={i}
                className="mt-5 rounded-xl bg-foreground p-4 text-[12.5px] leading-relaxed text-background overflow-x-auto"
              >
                {b.lang && (
                  <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-background/50">
                    {b.lang}
                  </div>
                )}
                <code>{b.code}</code>
              </pre>
            );
          case "callout":
            return (
              <div key={i} className="mt-6 rounded-xl hairline bg-cream p-5">
                {b.title && <div className="font-mono-eyebrow mb-2">{b.title}</div>}
                <div className="text-[14.5px] leading-relaxed text-foreground/85">{b.text}</div>
              </div>
            );
          case "table":
            return (
              <div key={i} className="mt-6 hairline rounded-xl overflow-x-auto">
                <table className="w-full text-[14px] min-w-[480px]">
                  <thead>
                    <tr className="bg-cream text-[13px] font-medium">
                      {b.head.map((h, j) => (
                        <th
                          key={j}
                          className={`p-3.5 text-left ${j > 0 ? "border-l border-border" : ""}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, ri) => (
                      <tr key={ri} className="border-t border-border">
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`p-3.5 align-top ${ci > 0 ? "border-l border-border text-muted-foreground" : "font-medium"}`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
        }
      })}
    </>
  );
}

export function DocsPageShell({
  slug,
  title,
  description,
  blocks,
}: {
  slug: string;
  title: string;
  description: string;
  blocks: DocBlock[];
}) {
  const [navOpen, setNavOpen] = useState(false);
  const idx = FLAT.findIndex((d) => d.slug === slug);
  const prev = idx > 0 ? FLAT[idx - 1] : null;
  const next = idx >= 0 && idx < FLAT.length - 1 ? FLAT[idx + 1] : null;

  // Auto-translate everything on the page (chrome, nav, title, body) into the
  // reader's active language in one batched, cached call.
  const CHROME = ["Documentation", "Previous", "Next", "Docs menu"];
  const navGroups = DOC_NAV.map((g) => g.group);
  const navTitles = FLAT.map((d) => d.title);
  const blockStrings = useMemo(() => collectBlockStrings(blocks as any), [blocks]);
  const strings = useMemo(
    () => [...CHROME, ...navGroups, ...navTitles, title, description, ...blockStrings],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, description, blockStrings],
  );
  const { out, busy } = useAutoTranslate(strings);
  let cur = 0;
  const chromeT = CHROME.map(() => out[cur++] ?? "");
  const navGroupsT = navGroups.map(() => out[cur++]);
  const navTitlesT = navTitles.map(() => out[cur++]);
  const titleT = out[cur++] ?? title;
  const descT = out[cur++] ?? description;
  const blocksT = applyBlockStrings(blocks as any, out.slice(cur)) as DocBlock[];
  const titleFor = (s: string) => {
    const i = FLAT.findIndex((d) => d.slug === s);
    return i >= 0 ? (navTitlesT[i] ?? FLAT[i].title) : s;
  };

  const Sidebar = (
    <nav className="space-y-6">
      {DOC_NAV.map((g, gi) => (
        <div key={g.group}>
          <div className="font-mono-eyebrow !text-[10px]">{navGroupsT[gi] ?? g.group}</div>
          <ul className="mt-2.5 space-y-0.5">
            {g.items.map((it) => {
              const active = it.slug === slug;
              return (
                <li key={it.slug}>
                  <Link
                    to={docPath(it.slug)}
                    onClick={() => setNavOpen(false)}
                    className={`block rounded-lg px-3 py-1.5 text-[13.5px] transition ${
                      active
                        ? "bg-card text-foreground font-medium hairline"
                        : "text-muted-foreground hover:text-foreground hover:bg-card"
                    }`}
                  >
                    {titleFor(it.slug)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <MarketingLayout>
      <div className="mx-auto max-w-6xl px-6 py-10 sm:py-14">
        {/* Mobile nav toggle */}
        <button
          onClick={() => setNavOpen((o) => !o)}
          className="lg:hidden inline-flex items-center gap-2 rounded-full hairline bg-card px-4 py-2 text-[13px] mb-6"
        >
          {navOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />} {chromeT[3]}
        </button>

        <div className="grid lg:grid-cols-[220px_minmax(0,1fr)] gap-10">
          <aside
            className={`${navOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-24 self-start`}
          >
            {Sidebar}
          </aside>

          <article className="min-w-0 max-w-3xl">
            <div className="font-mono-eyebrow flex items-center gap-2">
              {chromeT[0]}
              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            </div>
            <h1 className="mt-3 font-serif text-[36px] sm:text-[46px] leading-[1.03] tracking-[-0.03em]">
              {titleT}
            </h1>
            <p className="mt-4 text-[17px] leading-[1.55] text-muted-foreground">{descT}</p>

            <div className="mt-8">
              <Blocks blocks={blocksT} />
            </div>

            {/* Prev / next */}
            <div className="mt-14 pt-6 border-t border-border grid sm:grid-cols-2 gap-3">
              {prev ? (
                <Link
                  to={docPath(prev.slug)}
                  className="group hairline rounded-xl bg-card p-4 hover:bg-cream transition"
                >
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <ArrowLeft className="h-3 w-3" /> {chromeT[1]}
                  </div>
                  <div className="mt-1 font-medium text-[14.5px]">{titleFor(prev.slug)}</div>
                </Link>
              ) : (
                <span />
              )}
              {next && (
                <Link
                  to={docPath(next.slug)}
                  className="group hairline rounded-xl bg-card p-4 hover:bg-cream transition sm:text-right"
                >
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground sm:justify-end">
                    {chromeT[2]} <ArrowRight className="h-3 w-3" />
                  </div>
                  <div className="mt-1 font-medium text-[14.5px]">{titleFor(next.slug)}</div>
                </Link>
              )}
            </div>
          </article>
        </div>
      </div>
    </MarketingLayout>
  );
}
